import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, FileText, MoreVertical, Pencil, Trash2, Eye, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatRupiah, formatDateShort, formatApiError, STATUS_LABELS } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import ConfirmDialog from "@/components/ConfirmDialog";
import DataPagination from "@/components/DataPagination";
import StatusBadge from "@/components/StatusBadge";
import { TableSkeleton } from "@/components/Skeletons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const STATUSES = ["draft", "pending", "paid", "unpaid", "cancel"];

const Invoices = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [delId, setDelId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/invoices", { params: { search, status, page, limit: 8 } });
      setData(data);
    } finally {
      setLoading(false);
    }
  }, [search, status, page]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const changeStatus = async (id, s) => {
    try {
      await api.patch(`/invoices/${id}/status`, null, { params: { status: s } });
      toast.success("Status diperbarui");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/invoices/${delId}`);
      toast.success("Invoice dihapus");
      load();
    } finally {
      setDelId(null);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Invoice" subtitle="Buat dan kelola tagihan pelanggan">
        <Button onClick={() => navigate("/invoices/new")} data-testid="add-invoice-btn">
          <Plus className="mr-2 h-4 w-4" /> Buat Invoice
        </Button>
      </PageHeader>

      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Cari no. invoice / customer..." className="pl-9" value={search} data-testid="invoice-search-input" onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
        </div>
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
          <SelectTrigger className="w-full sm:w-44" data-testid="invoice-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Status</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-xl border border-border bg-card p-2 shadow-[0_2px_10px_rgba(0,0,0,0.03)]">
        {loading ? (
          <div className="p-4"><TableSkeleton cols={6} /></div>
        ) : data.items.length === 0 ? (
          <EmptyState icon={FileText} title="Belum ada invoice" description="Buat invoice pertama Anda untuk menagih pelanggan." action={<Button onClick={() => navigate("/invoices/new")}><Plus className="mr-2 h-4 w-4" />Buat Invoice</Button>} />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>No. Invoice</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Tanggal</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((inv) => (
                <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`} className="cursor-pointer" onClick={() => navigate(`/invoices/${inv.id}`)}>
                  <TableCell className="font-mono text-xs font-medium text-primary">{inv.number}</TableCell>
                  <TableCell>{inv.customer_name}</TableCell>
                  <TableCell>{formatDateShort(inv.date)}</TableCell>
                  <TableCell className="text-right font-semibold">{formatRupiah(inv.grand_total)}</TableCell>
                  <TableCell><StatusBadge status={inv.status} /></TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" data-testid={`invoice-actions-${inv.id}`}><MoreVertical className="h-4 w-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/invoices/${inv.id}`)}><Eye className="mr-2 h-4 w-4" />Lihat</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(`/invoices/${inv.id}/edit`)}><Pencil className="mr-2 h-4 w-4" />Edit</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => changeStatus(inv.id, "paid")}><CheckCircle2 className="mr-2 h-4 w-4" />Tandai Lunas</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => setDelId(inv.id)} className="text-destructive"><Trash2 className="mr-2 h-4 w-4" />Hapus</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {!loading && <DataPagination page={page} limit={8} total={data.total} onPage={setPage} />}
      <ConfirmDialog open={!!delId} onOpenChange={() => setDelId(null)} title="Hapus Invoice?" description="Tindakan ini tidak dapat dibatalkan." onConfirm={doDelete} />
    </div>
  );
};

export default Invoices;
